import { useEffect, useState } from 'react';
import { useApiContext } from '../../../api';
import { TpAssetInfo, TpAssetPropertyInfo } from '../../../data/assets/interfaces';
import { useAppDispatch } from '../../../app/hooks';
import { modifyOpenedAsset } from '../../../data/assets/queriedAssetsSlice';
import { addPollingConfiguration, PollingConfiguration, removePollingConfiguration } from '../../../data/pollingSlice';
import { ETLAssetPollingId } from '../../../data/pollingService';
import { computeStringHash } from '../../etl-pipeline/utils';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../components/auto-form-modal/AutoForm';
import { removeDataContext } from '../../../data/analyticsDataSlice';
import { useUser } from '../../../app/hooks';
import PipelineBuilder from '../../etl-pipeline/pipeline-builder/PipelineBuilder';
import Dashboard from '../../visual-analytics/dashboard/Dashboard';
import { TpSectionsIndexContext } from '../../../components/sections-index/SectionsIndex';
import styles from '../AssetView.module.scss';
import { TpTemplateInfo } from '../../../api/models/assets';
import Tabber from '../../../components/panels/tabber/Tabber';
import { modifyTemplate } from '../../../data/templateSlice';

interface TpAssetPipelineBuilderProps {
  assetInfo: TpAssetInfo;
  sectionIndexContext: TpSectionsIndexContext;
}

interface PipelineState {
  etlConfig?: string;
  lastExecutedConfig?: string;
  reportLink?: string;
  lastComponentProgress?: Record<string, unknown>;
  runningComponentProgress?: Record<string, unknown>;
}

const workflowTabId = 'workflow';
const dashboardTabId = 'dashboard';

export const AssetPipelineBuilder = (props: TpAssetPipelineBuilderProps) => {
  const { assetInfo, sectionIndexContext } = props;
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const [dashboardHash, setDashboardHash] = useState<string>('');
  const { user } = useUser();

  // Add new states for tab-related data
  const [canEditPipeline, setCanEditPipeline] = useState(false);
  const [hasDashboard, setHasDashboard] = useState(false);
  const [tabIds, setTabIds] = useState<string[]>([]);

  const tabTitles: Map<string, string> = new Map([
    [workflowTabId, 'Workflow'],
    [dashboardTabId, 'Dashboard'],
  ]);

  // Extract pipeline state from asset properties
  const pipelineState: PipelineState = {
    etlConfig: assetInfo.properties['etl_config'],
    lastExecutedConfig: assetInfo.properties['last_etl_run']?.['etl_config'],
    reportLink: assetInfo.properties['last_etl_run']?.['task_properties']?.['pdf_report'],
    lastComponentProgress: assetInfo.properties['last_etl_run']?.['task_properties']?.['component_progress'],
    runningComponentProgress: assetInfo.properties['current_etl_task']?.['component_progress'],
  };

  const updateTemplate = (templateString: string) => {
    const updatedAssetInfo: TpAssetInfo = {
      ...assetInfo!,
      properties: {
        ...assetInfo!.properties,
        templates: {
          ...assetInfo!.properties?.templates,
          ['dashboard_template']: templateString,
        },
      },
    };
    dispatch(modifyOpenedAsset({ asset: updatedAssetInfo }));
  };

  const saveAssetTemplate = (templateString: string) => {
    const existingTemplate = assetInfo.assetDashboardTemplate;
    const newTemplate = { ...existingTemplate, yamlString: templateString };
    return api.asset.modifyAssetTemplate(assetInfo.id, newTemplate as TpTemplateInfo).then((updatedAssetInfo) => {
      dispatch(modifyOpenedAsset({ asset: updatedAssetInfo }));
      if (updatedAssetInfo.assetDashboardTemplate) {
        dispatch(modifyTemplate({ template: updatedAssetInfo.assetDashboardTemplate }));
      }
      return templateString;
    });
  };

  const startPipelinePolling = () => {
    const pollingConfig: PollingConfiguration = {
      identifier: ETLAssetPollingId,
      interval: 3000,
      params: { assetId: assetInfo.id },
    };
    dispatch(addPollingConfiguration(pollingConfig));
  };

  const saveAssetPipeline = (pipeline: string) => {
    const propertyInfo: TpAssetPropertyInfo = {
      key: ['etl_config'],
      value: pipeline,
    };
    return api.asset.setAssetProperty(assetInfo.id, propertyInfo).then((resp: TpAssetInfo) => {
      dispatch(modifyOpenedAsset({ asset: resp }));
    });
  };

  const executePipeline = (pipeline: string, modifiedComponents?: string[]) => {
    const executeFullPipeline = () =>
      saveAssetPipeline(pipeline).then(() =>
        api.etl.executeFullPipelineForAsset(assetInfo.id).then(startPipelinePolling),
      );

    const executePartialPipeline = () =>
      saveAssetPipeline(pipeline).then(() =>
        api.etl.executePartialPipelineForAsset(assetInfo.id, modifiedComponents || []).then(startPipelinePolling),
      );

    if (modifiedComponents && modifiedComponents.length > 0) {
      const form: TpAutoFormDef = {
        title: `Execute Partial Pipeline for ${assetInfo.name}`,
        description: 'Only modified components will be executed. Enforce a full run by clicking on the checkbox below.',
        fields: [
          {
            id: 'fullRun',
            name: 'Enforce Full Run',
            dataType: FORM_DATATYPES.CHECKBOX,
            startContent: false,
            required: false,
          },
        ],
      };

      return promptAutoForm(form).then((dataForm: unknown) => {
        const typedDataForm = dataForm as { fullRun: boolean };
        return typedDataForm.fullRun ? executeFullPipeline() : executePartialPipeline();
      });
    }

    return executeFullPipeline();
  };

  // Handle pipeline monitoring
  useEffect(() => {
    const newDashboardHash = assetInfo.properties['last_etl_run']
      ? computeStringHash(assetInfo.properties['last_etl_run'])
      : '';

    if (dashboardHash !== newDashboardHash) {
      setDashboardHash(newDashboardHash);
    }

    if (!pipelineState.runningComponentProgress) {
      dispatch(removePollingConfiguration({ pollingConfigId: ETLAssetPollingId }));
      dispatch(removeDataContext(assetInfo.id));
    } else {
      startPipelinePolling();
    }
  }, [
    assetInfo.properties['etl_config'],
    assetInfo.properties['last_etl_run'],
    assetInfo.properties['current_etl_task'],
    dashboardHash,
    dispatch,
    assetInfo.id,
  ]);

  // New useEffect for tab-related computations
  useEffect(() => {
    const canEdit = (user.canWrite && user.email === assetInfo.owner.toLowerCase()) || user.isOrganizationAdmin;
    const hasDash = Boolean(assetInfo.properties['data_context']);

    setCanEditPipeline(canEdit);
    setHasDashboard(hasDash);

    const tabs = [...(canEdit ? [workflowTabId] : []), ...(hasDash ? [dashboardTabId] : [])];
    setTabIds(tabs);
  }, [assetInfo, user]);

  const dashboardTemplate = {
    property_key: 'data_context',
    onMissingContent: {
      placeholder: 'Run ETL pipeline to populate this dashboard',
      showError: false,
    },
  };

  if (!canEditPipeline && !hasDashboard) return null;

  const pipelineBuilderComponent = (
    <PipelineBuilder
      saveAssetPipeline={saveAssetPipeline}
      assetPipelineConfig={pipelineState.etlConfig}
      assetInfo={assetInfo}
      executePipeline={executePipeline}
      lastRunConfig={pipelineState.lastExecutedConfig}
      reportLink={pipelineState.reportLink}
      lastComponentProgress={pipelineState.lastComponentProgress}
      runningComponentProgress={pipelineState.runningComponentProgress}
    />
  );

  const dashboardComponent = (
    <Dashboard
      isAssetTemplate={true}
      dashboardtemplate={dashboardTemplate}
      assetInfo={assetInfo}
      sectionIndexContext={sectionIndexContext}
      onTemplateUpdate={updateTemplate}
      onTemplateSave={saveAssetTemplate}
    />
  );

  return (
    <div className={styles.pipelineBuilderWrapper}>
      <Tabber
        tabIds={tabIds}
        getHeader={(tabId) => tabTitles.get(tabId) || tabId}
        renderContent={(tabId) => {
          if (tabId === workflowTabId) {
            return pipelineBuilderComponent;
          } else if (tabId === dashboardTabId) {
            return dashboardComponent;
          }
          return null;
        }}
      />
    </div>
  );
};
